import { action } from "typesafe-actions";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import { ICompanyOutlet } from "./companyOutlet.types";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { error_message } from "../../constants/constants";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";
import { uploadFile } from "../../components/FileUpload/utils";

// Action Types
export const FETCH_COMPANY_OUTLET_LIST_PROGRESS =
  "FETCH_COMPANY_OUTLET_LIST_PROGRESS";
export const FETCH_COMPANY_OUTLET_LIST_SUCCESS =
  "FETCH_COMPANY_OUTLET_LIST_SUCCESS";
export const FETCH_COMPANY_OUTLET_LIST_FAILED =
  "FETCH_COMPANY_OUTLET_LIST_FAILED";

export const FETCH_COMPANY_OUTLET_PROGRESS = "FETCH_COMPANY_OUTLET_PROGRESS";
export const FETCH_COMPANY_OUTLET_SUCCESS = "FETCH_COMPANY_OUTLET_SUCCESS";
export const FETCH_COMPANY_OUTLET_FAILED = "FETCH_COMPANY_OUTLET_FAILED";

// Action Creators
export const fetchCompanyOutletListProgress = () =>
  action(FETCH_COMPANY_OUTLET_LIST_PROGRESS);
export const fetchCompanyOutletListSuccess = (
  data: ICompanyOutlet[],
  totalRecords: number,
) => action(FETCH_COMPANY_OUTLET_LIST_SUCCESS, { data, totalRecords });
export const fetchCompanyOutletListFailed = (errorMessage: string) =>
  action(FETCH_COMPANY_OUTLET_LIST_FAILED, { errorMessage });

export const fetchCompanyOutletProgress = () =>
  action(FETCH_COMPANY_OUTLET_PROGRESS);
export const fetchCompanyOutletSuccess = (data: ICompanyOutlet) =>
  action(FETCH_COMPANY_OUTLET_SUCCESS, { data });
export const fetchCompanyOutletFailed = (errorMessage: any) =>
  action(FETCH_COMPANY_OUTLET_FAILED, { errorMessage });

// Async Actions
export const fetchCompanyOutletListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    limit: number,
    status: string,
    date?: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
    searchByName?: string,
    company_outlet_uuid?: string | null,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch, getState) => {
      try {
        let searchQuery = "";
        if (status && status !== "-1" && status !== "All" && status !== "all") {
          searchQuery += `&status=${encodeURIComponent(status.toUpperCase())}`;
        }

        if (company_outlet_uuid) {
          searchQuery += `&company_outlet_uuid=${company_outlet_uuid}`;
        }

        if ((searchValue?.length ?? 0) > 0 || searchByName) {
          const advanceFilter = [...(searchValue || [])]; // Ensure it's always an array

          if (searchByName) {
            advanceFilter.push({
              column: ["company_outlet_name"],
              value: searchByName,
              logicalOperator: "AND",
              operator: "CONTAINS",
            });
          }

          searchQuery += `&advanceFilter=${encodeURIComponent(JSON.stringify(advanceFilter))}`;
        }

        if (date) {
          searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
        }

        const finalUrl =
          // `DataTableV2` pagination uses "fetch one extra row" (limit = rowsPerPage + 1)
          // so we must ask the API for `limit` records, not just `rowsInPerPage`.
          `/dataManagement/get-company-outlet?pageNo=${pageNumber}&pageLimit=${limit}&itemPerPage=${limit}` +
          searchQuery;

        dispatch(fetchCompanyOutletListProgress());
        // dispatch(saveLoaderProgress());
        const res = await api.get(finalUrl);
        const data: ICompanyOutlet[] = res.data.data;
        const totalRecords = res.data.totalRecords;

        dispatch(fetchCompanyOutletListSuccess(data, totalRecords));
      } catch (err: any) {
        dispatch(
          fetchCompanyOutletListFailed(
            err?.response?.data?.message ?? error_message,
          ),
        );
        dispatch(
          showMessage({
            type: "error",
            message: err?.response?.data?.message ?? error_message,
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const fetchCompanyOutletAsync =
  (companyOutletUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      try {
        dispatch(fetchCompanyOutletProgress());
        dispatch(saveLoaderProgress());
        const res = await api.get(
          `/dataManagement/get-company-outlet?company_outlet_uuid=${companyOutletUuid}`,
        );
        const data = res.data.data;
        if (data.length > 0) {
          dispatch(fetchCompanyOutletSuccess(data[0]));
        } else {
          dispatch(fetchCompanyOutletFailed(res.data.message));
        }
      } catch (err: any) {
        dispatch(fetchCompanyOutletFailed(err));
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const upsertCompanyOutletAsync =
  (
    companyOutlet: ICompanyOutlet,
    logo: any | null,
    onCallback: (isSuccess?: boolean, data?: ICompanyOutlet) => void,
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
    async (dispatch) => {
      dispatch(saveLoaderProgress());
      try {
        const asPayload = {
          company_outlet_name: companyOutlet.company_outlet_name,
        };
        const path = await uploadFile({
          file: logo,
          module_name: "COMPANY_OUTLET_LOGO",
          defaultReturn: companyOutlet.logo || "",
          asPayload: asPayload,
          onWarning: (message) => {
            dispatch(
              showMessage({
                type: "warning",
                message: message,
                displayAs: "snackbar",
              }),
            );
          },
        });
        const { create_ts, insert_ts, created_by_uuid, ...rest } = companyOutlet;
        const res = await api.post("/dataManagement/upsert-company-outlet", {
          ...rest,
          logo: path,
        });
        dispatch(
          showMessage({
            type: "success",
            message: res.data.message,
            displayAs: "snackbar",
          }),
        );
        const data = res.data.data;
        onCallback(true, data);
      } catch (err: any) {
        onCallback(false);
        dispatch(
          showMessage({
            type: "error",
            message: err.response.data.message,
            displayAs: "snackbar",
          }),
        );
      } finally {
        dispatch(saveLoaderCompleted());
      }
    };

export const CLEAR_COMPANY_OUTLET_STATE = "CLEAR_COMPANY_OUTLET_STATE";
export const CLEAR_COMPANY_OUTLET = "CLEAR_COMPANY_OUTLET";

export const clearCompanyOutletState = () => action(CLEAR_COMPANY_OUTLET_STATE);
export const clearCompanyOutlet = () => action(CLEAR_COMPANY_OUTLET);
